
import { Component, namespace, Prop } from 'nuxt-property-decorator'
import axios from 'axios'
import { createAmpOfferInstance, Offer } from '@alao-frontend/core'
import { OnCreated, OnMounted } from '~/core/vue.types'
import { $api } from '~/plugins/axios'
import { FeaturesBannerItem } from '~/components/sections/FeaturesBanner.vue'
import { namespace as AmpCommonFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Common'
import { namespace as AmpMobileFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Mobile'
import { namespace as AmpHomeFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Home'
import {
  CommonFiltersState as CommonFiltersStateType,
  HomeFiltersState as HomeFiltersStateType,
  MobileFiltersState as MobileFiltersStateType,
} from '~/core/config/filters/types'
import { OfferType, OfferTypeByShortName, OfferTypeKey } from '~/core/offer.types'
import OfferCard from '~/components/offers/OfferCard/OfferCard.vue'
import SkeletonBox from '~/components/comparator/SkeletonBox.vue'
import {
  ActionType as OfferListActionType,
  GettersType as OfferListGetterType,
  MutationType as OfferListMutationType,
  namespace as offerListNamespace,
  OfferState,
  PublicAction as OfferListPublicAction,
  PublicGetters as OfferListPublicGetters,
} from '~/store/modules/Amp/OfferList'

import AppAlert from '~/components/AppAlert.vue'
import LoadMoreButton from '~/components/snippets/LoadMoreButton.vue'
import { commonFiltersToRequest, homeFiltersToRequest, mobileFiltersToRequest } from '~/utils/filters-driver.utils'
import { addressObservable } from '~/observables/modules/address'
import { FILTRATION_ITEM_PARAMS } from '~/types/filters.types'
import { OffersListAnalyticsMixin } from '~/mixins/offers-list-analytics.mixin'
import { AlButton } from '~/components/ui'
import { REDIRECT_QUERY_PARAM_SLUG, REDIRECT_REASONS } from '~/core/config/redirect-reasons'

const OfferListNamespace = namespace(offerListNamespace)
const commonFiltersModuleNs = namespace(AmpCommonFiltersModuleNamespace)
const homeFiltersModuleNs = namespace(AmpHomeFiltersModuleNamespace)
const mobileFiltersModuleNs = namespace(AmpMobileFiltersModuleNamespace)

const CancelToken = axios.CancelToken
let cancelTokenSource: any = null

const ComparatorBanner1 = () => ({
  component: import(
    /* webpackChunkName: "comparator-banners" */ '~/views/comparator/banners/ComparatorBanner1.vue'
  ),
  delay: 0,
  timeout: 3000,
})
const ComparatorBanner2 = () => ({
  component: import(
    /* webpackChunkName: "comparator-banners" */ '~/views/comparator/banners/ComparatorBanner2.vue'
  ),
  delay: 0,
  timeout: 3000,
})
const ComparatorBanner3 = () => ({
  component: import(
    /* webpackChunkName: "comparator-banners" */ '~/views/comparator/banners/ComparatorBanner3.vue'
  ),
  delay: 0,
  timeout: 3000,
})
const FeaturesBanner = () => ({
  component: import(
    /* webpackChunkName: "comparator-banners" */ '~/components/sections/FeaturesBanner.vue'
  ),
  delay: 0,
  timeout: 3000,
})

let debounce: any = null

@Component({
  components: {
    AlButton,
    LoadMoreButton,
    AppAlert,
    OfferCard,
    SkeletonBox,
    Banner1: ComparatorBanner1,
    Banner2: ComparatorBanner2,
    Banner3: ComparatorBanner3,
    FeaturesBanner,
  },
})
export default class ComparatorOfferList extends OffersListAnalyticsMixin implements OnMounted, OnCreated {
  @Prop({
    required: true,
    type: String,
  })
  protected selectedOfferType!: OfferTypeKey

  @Prop({
    default: 10,
    type: Number,
  })
  readonly perPage!: number

  @Prop({
    default: false,
    type: Boolean,
  })
  readonly noBanners!: boolean

  private page = 1
  private offset = 0

  private specificOffer: Offer | null = null
  private isInstantSearch = false
  private isAddressChanged = true
  private homeProviderSpeeds: Record<string, number> = {}

  protected isResultsMatchingFilter = true
  protected isResultsAvailable = true
  protected isShowNoResultsAlert = false

  isShowOfferNotAvailableAlert = false

  private featureBannersStyles = {
    '--features-icon-size': '32px',
    '--features-padding-y': '1.5rem',
  }

  @commonFiltersModuleNs.Getter('GET_FILTERS')
  readonly getCommonFilters!: CommonFiltersStateType

  @homeFiltersModuleNs.Getter('GET_FILTERS')
  readonly getHomeFilters!: HomeFiltersStateType

  @mobileFiltersModuleNs.Getter('GET_FILTERS')
  readonly getMobileFilters!: MobileFiltersStateType

  @OfferListNamespace.Action(OfferListActionType.FETCH_OFFERS)
  fetchOffersStore!: OfferListPublicAction[OfferListActionType.FETCH_OFFERS]

  @OfferListNamespace.Mutation('CLEAR_STATE')
  clearState!: () => void

  @OfferListNamespace.Getter(OfferListGetterType.GET_OFFERS)
  readonly offersFromStore!: OfferListPublicGetters[OfferListGetterType.GET_OFFERS]

  @OfferListNamespace.Getter(OfferListGetterType.IS_LOADING)
  readonly isLoading!: OfferListPublicGetters[OfferListGetterType.IS_LOADING]

  @OfferListNamespace.Getter(OfferListGetterType.GET_OFFERS_COUNT)
  readonly offersCount!: OfferListPublicGetters[OfferListGetterType.GET_OFFERS_COUNT]

  @OfferListNamespace.Getter(OfferListGetterType.GET_OFFERS_SORT)
  readonly offersSort!: OfferListPublicGetters[OfferListGetterType.GET_OFFERS_SORT]

  @OfferListNamespace.Mutation(OfferListMutationType.SET_LOADING)
  setLoading!: (value: boolean) => void

  @homeFiltersModuleNs.Mutation('SET_FILTER')
  setHomeFilters!: ({
    value,
    key,
  }: { value: string[], key: string }) => void

  get offers (): OfferState['offers'] {
    let offers = []

    offers.push(...(this.offersFromStore as any[]))

    if (this.specificOffer) {
      offers = offers.filter(offer => offer.slug !== this.specificOffer!.slug)

      offers.unshift(this.specificOffer)
    }

    return offers
  }

  get bannersPosition () {
    if (this.$mqUtils.isMobile) {
      return [2, 4, 6]
    } else {
      return [1, 3, 5]
    }
  }

  async triggerInstantSearch () {
    if (cancelTokenSource?.cancel) {
      cancelTokenSource.cancel()
    }

    this.clearState()
    this.isInstantSearch = true
    this.page = 1
    this.offset = 0
    await this.debouncedFetchOffers()
    this.isInstantSearch = false
  }

  get instantSearchPropsToWatch () {
    return {
      common: {
        ...this.getCommonFilters,
      },
      home: {
        ...this.getHomeFilters,
      },
      mobile: {
        ...this.getMobileFilters,
      },
      sort: this.offersSort,
    }
  }

  get specificOfferSlug () {
    return this.$route.query.selected_offer
  }

  async fetchSpecificOffer () {
    if (!this.specificOfferSlug || this.page !== 1) {
      return null
    }

    try {
      return await $api.PublicOfferService.getOffer({
        productType: OfferType[this.selectedOfferType],
        slug: this.specificOfferSlug.toString(),
      })
    } catch (error) {
      // TODO: Handle error
      return null
    }
  }

  scrollToResults () {
    const top = document.querySelector<Element>('#sort-by')

    if (top) {
      this.$scrollTo(top, 500, { offset: -75 })
    }
  }

  handleOffersRequestResponse (response: Record<string, any>) {
    const results = response.items

    // Reset no results alert state
    this.isShowNoResultsAlert = false

    this.isResultsMatchingFilter = response.result_match_filter
    this.isResultsAvailable = Boolean(results.length)

    if (
      !this.isResultsMatchingFilter ||
      !this.isResultsAvailable
    ) {
      this.isShowNoResultsAlert = true
    }
  }

  get isMobile () {
    return this.selectedOfferType === OfferTypeByShortName.mobile
  }

  get isHome () {
    return this.selectedOfferType === OfferTypeByShortName.home
  }

  get isBundle () {
    return this.selectedOfferType === OfferTypeByShortName.bundle
  }

  debouncedFetchOffers () {
    if (debounce) {
      clearTimeout(debounce)
    }
    debounce = setTimeout(this.fetchOffers, 600)
  }

  async fetchOffers (isLoadMore = false) {
    this.setLoading(true)
    try {
      if (cancelTokenSource?.cancel) {
        cancelTokenSource.cancel()
      }
      cancelTokenSource = CancelToken.source()

      const isScrollToTop = false

      let payload: any = Object.assign(
        {},
        {
          offset: this.offset,
          sort: this.offersSort,
        },
        commonFiltersToRequest(this.getCommonFilters, true),
      )

      // TODO reduce
      if (this.isMobile) {
        payload = Object.assign({}, payload, mobileFiltersToRequest(this.getMobileFilters, true))
      }
      if (this.isHome) {
        payload = Object.assign({}, payload, homeFiltersToRequest(this.getHomeFilters, true))
      }
      if (this.isBundle) {
        payload = Object.assign({},
          payload,
          mobileFiltersToRequest(this.getMobileFilters, true),
          homeFiltersToRequest(this.getHomeFilters, true),
        )
      }

      if (this.isAddressChanged && (this.isHome || this.isBundle)) {
        this.homeProviderSpeeds = {}

        if (
          addressObservable.zip.length >= 4 &&
        addressObservable.city &&
        addressObservable.street &&
        addressObservable.number
        ) {
          try {
            const { providers } = await $api.PublicAddressService.addressCheckByAllProvidersRuntime({
              zip: addressObservable.zip,
              city: addressObservable.city,
              street: addressObservable.street,
              house: addressObservable.number,
            }, { cancelToken: cancelTokenSource.token })

            this.homeProviderSpeeds = providers.reduce((acc: Record<string, number>, provider) => {
              const speed = Math.max(...provider.connections.map(connection => Number(connection.download_speed)), 0)

              acc[provider.provider] = speed

              return acc
            }, {})

            this.isAddressChanged = false
          } catch (e) {
            console.info('Address check canceled', e)
            return
          }
        }
      }

      payload = {
        ...payload,
        limit: this.perPage,
        expert_tip: this.$route.query[FILTRATION_ITEM_PARAMS.expert_tip]
          ? this.$route.query[FILTRATION_ITEM_PARAMS.expert_tip]
          : undefined,
        home_provider_speeds: Object.keys(this.homeProviderSpeeds).length ? this.homeProviderSpeeds : undefined,
      }

      // remove all false or empty fields
      let key: string
      for (key in payload) {
        if (typeof payload[key] === 'number' && isNaN(payload[key])) {
          delete payload[key]
        } if (typeof payload[key] !== 'number' && !payload[key]) {
          delete payload[key]
        }
      }

      payload = {
        payload,
        offerType: OfferType[this.selectedOfferType],
        isLoadMore,
        cancelToken: cancelTokenSource.token,
      }

      if (this.page === 1) {
        const specificOffer = await this.fetchSpecificOffer()

        if (specificOffer) {
          this.specificOffer = createAmpOfferInstance(specificOffer)
        }
      }
      const offers = await this.fetchOffersStore(payload)
      if (!offers) {
        return
      }
      this.handleOffersRequestResponse(offers)

      if (this.page === 1 && isScrollToTop) {
        this.scrollToResults()
      }

      this.setLoading(false)
    } catch (e:any) {
      console.info('Search canceled', e)
    }
  }

  setInstantSearchWatcher () {
    this.$watch(
      () => this.instantSearchPropsToWatch,
      this.triggerInstantSearch,
      { deep: true },
    )
    this.$watch(() => addressObservable?.number, () => {
      this.isAddressChanged = true
      this.triggerInstantSearch()
    })
  }

  async onNextPage () {
    this.page = this.page + 1
    this.offset = (this.page - 1) * this.perPage
    await this.fetchOffers(true)
  }

  get isSliderView () {
    return this.$mqUtils.isMobile
  }

  get featuresBannerItems (): FeaturesBannerItem[] {
    return [
      {
        icon: require('~/assets/images/icons/features-banner/features-banner-icon-time.svg'),
        title: this.$t('compare.banner.other.1.title') as string,
        description: this.$t('compare.banner.other.1.description') as string,
      },
      {
        icon: require('~/assets/images/icons/features-banner/features-banner-icon-crown.svg'),
        title: this.$t('compare.banner.other.2.title') as string,
        description: this.$t('compare.banner.other.2.description') as string,
      },
      {
        icon: require('~/assets/images/icons/features-banner/features-banner-icon-lock.svg'),
        title: this.$t('compare.banner.other.3.title') as string,
        description: this.$t('compare.banner.other.3.description') as string,
      },
      {
        icon: require('~/assets/images/icons/features-banner/features-banner-icon-headphones.svg'),
        title: this.$t('compare.banner.other.4.title') as string,
        description: this.$t('compare.banner.other.4.description') as string,
      },
    ]
  }

  created () {
    this.clearState()
  }

  async mounted () {
    if (REDIRECT_QUERY_PARAM_SLUG in this.$route.query && this.$route.query[REDIRECT_QUERY_PARAM_SLUG] === REDIRECT_REASONS.OFFER_NOT_AVAILABLE) {
      this.isShowOfferNotAvailableAlert = true

      const query = Object.assign({}, this.$route.query)
      delete query[REDIRECT_QUERY_PARAM_SLUG]

      const routeName = (this.$route.name || '').replace(/___.*/g, '')

      if (!routeName) {
        return
      }

      window.history.replaceState({}, '', this.localePath({ name: routeName as string, query }))
    }

    await this.fetchOffers()

    this.$nextTick(() => {
      this.setInstantSearchWatcher()
    })
  }
}
